import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PageShell } from './layout';
import { GAME_CONFIG } from '../games';
import { getCurrentDate } from '../util';
import { darkenColor, isLightColor } from '../utils/colors';
import GameCatalogSection from './GameCatalog';
import FaIcon from './FaIcon';
import { useStellineRewards } from '../contexts/StellineRewardsContext';
import StellineRewardChip from './StellineRewardChip';
import styles from '../styles/GameLandingPage.module.css';
import { useAds } from '../contexts/AdsContext';
import { GOOGLE_INTERSTITIAL_INELIGIBLE_ATTRIBUTES } from '../ads/googleInterstitialAttributes';

const GameLandingPage = () => {
  const location = useLocation();
  const currentDate = getCurrentDate();
  const { getGameWinReward, getGameWinMaxReward, getGameWinRewardRange } = useStellineRewards();
  const { navigationInterstitialMode, registerNavigationInterstitialCandidate } = useAds();

  // Extract game path from URL (e.g., /collegamenti -> collegamenti)
  const gamePath = location.pathname.split('/').filter(Boolean)[0];

  // Find the game config by pathName
  const gameConfig = useMemo(() => {
    return Object.values(GAME_CONFIG).find(g => g.pathName === gamePath);
  }, [gamePath]);

  // If game not found, redirect to home
  if (!gameConfig) {
    return null; // Will be handled by NotFound route
  }

  const {
    displayName,
    backgroundColor,
    icon: GameIcon,
    heroTagline,
    heroDescription,
    benefits,
    targetAudience
  } = gameConfig;

  const isDarkText = isLightColor(backgroundColor);
  const darkerBg = darkenColor(backgroundColor, 15);
  const ctaRewardAmount = gameConfig.id === 'ortografia'
    ? null
    : getGameWinReward(gameConfig.id);
  const ctaRewardRange = gameConfig.id === 'ortografia'
    ? getGameWinRewardRange(gameConfig.id)
    : null;
  const ctaMaxRewardAmount = gameConfig.id === 'ortografia'
    ? getGameWinMaxReward(gameConfig.id)
    : null;
  const usesAnchorManagedInterstitial = navigationInterstitialMode === 'anchor';
  const registerCandidate = (exitType, targetPath) => {
    if (!usesAnchorManagedInterstitial) {
      return undefined;
    }

    return registerNavigationInterstitialCandidate({
      gameSlug: gameConfig.id,
      date: currentDate,
      exitType,
      targetPath,
    });
  };

  return (
    <PageShell>
      <div data-google-interstitial="false">
        {/* Hero Section */}
        <section 
          className={`${styles.heroSection} ${isDarkText ? styles.darkText : ''}`}
        >
          <div className={styles.heroMain} style={{ backgroundColor }}>
            {/* Parallax background shapes */}
            <div className={styles.heroBackground}>
              <div 
                className={`${styles.parallaxShape} ${styles.shape1}`}
                style={{ backgroundColor: darkerBg }}
              />
              <div 
                className={`${styles.parallaxShape} ${styles.shape2}`}
                style={{ backgroundColor: darkerBg }}
              />
              <div 
                className={`${styles.parallaxShape} ${styles.shape3}`}
                style={{ backgroundColor: darkerBg }}
              />
              <div 
                className={`${styles.parallaxShape} ${styles.shape4}`}
                style={{ backgroundColor: darkerBg }}
              />
              <div 
                className={`${styles.parallaxShape} ${styles.shape5}`}
                style={{ backgroundColor: darkerBg }}
              />
            </div>

            <div className={styles.heroGradient} />

            <div className={styles.heroContent}>
              <div className={styles.iconContainer}>
                <div className={styles.gameIcon} style={{ backgroundColor: darkerBg }}>
                  {GameIcon && <GameIcon size={56} />}
                </div>
              </div>

              <h1 className={styles.heroTitle}>{displayName}</h1>
              
              <p 
                className={styles.heroTagline}
                style={{ color: isDarkText ? '#333' : 'rgba(255,255,255,0.95)' }}
              >
                {heroTagline}
              </p>
              
              <p className={styles.heroDescription}>
                {heroDescription}
              </p>

              <div className={styles.heroButtons}>
                <div className={styles.ctaPrimaryWrap}>
                  <Link 
                    to={`/${gameConfig.pathName}/${currentDate}`}
                    className={styles.ctaButton}
                    style={{ backgroundColor: darkerBg }}
                    data-google-vignette={usesAnchorManagedInterstitial ? undefined : 'false'}
                    onClick={() => registerCandidate('landing_play', `/${gameConfig.pathName}/${currentDate}`)}
                  >
                    <span>Gioca ora</span>
                    <FaIcon className={`fas fa-play ${styles.ctaIcon}`}  />
                  </Link>
                  <StellineRewardChip
                    amount={ctaRewardAmount != null ? ctaRewardAmount : ctaMaxRewardAmount}
                    minAmount={ctaRewardRange?.min}
                    maxAmount={ctaRewardRange?.max}
                    isMax={ctaRewardAmount == null && ctaMaxRewardAmount != null}
                    className={styles.ctaRewardChip}
                  />
                </div>
                <Link 
                  to={`/${gameConfig.pathName}/archivio`}
                  className={`${styles.secondaryButton} ${isDarkText ? styles.darkSecondary : ''}`}
                  data-google-vignette={usesAnchorManagedInterstitial ? undefined : 'false'}
                  onClick={() => registerCandidate('archive', `/${gameConfig.pathName}/archivio`)}
                >
                  <span>Archivio</span>
                  <FaIcon className="fas fa-calendar-alt"  />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Target Audience Section */}
        <section className={styles.audienceSection}>
          <div className={styles.audienceCard}>
            <div className={styles.audienceIcon}>🎯</div>
            <h2 className={styles.audienceTitle}>Per chi è questo gioco?</h2>
            <p className={styles.audienceText}>{targetAudience}</p>
          </div>
        </section>

        {/* Benefits Section */}
        <section className={styles.benefitsSection}>
          <div className={styles.sectionContainer}>
            <h2 className={styles.sectionTitle}>Perché giocare a {displayName}?</h2>
            <p className={styles.sectionSubtitle}>
              Un modo efficace e divertente per migliorare il tuo italiano ogni giorno.
            </p>

            <div className={styles.benefitsGrid}>
              {benefits && benefits.map((benefit, index) => (
                <div key={index} className={styles.benefitCard}>
                  <div 
                    className={styles.benefitIconWrapper}
                    style={{ backgroundColor }}
                  >
                    <FaIcon className={`fas ${benefit.icon} ${styles.benefitIcon}`} />
                  </div>
                  <h3 className={styles.benefitTitle}>{benefit.title}</h3>
                  <p className={styles.benefitDescription}>{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>


        {/* Archive Section */}
        <section className={styles.archiveSection}>
          <div className={styles.sectionContainer}>
            <div className={styles.archiveCard}>
              <div className={styles.archiveIcon}>📅</div>
              <h2 className={styles.archiveTitle}>Archivio</h2>
              <p className={styles.archiveText}>
                Esplora le partite dei giorni precedenti e mettiti alla prova con i giochi che ti sei perso.
              </p>
              <Link 
                to={`/${gameConfig.pathName}/archivio`}
                className={styles.archiveLink}
                style={{ backgroundColor }}
                data-google-vignette={usesAnchorManagedInterstitial ? undefined : 'false'}
                onClick={() => registerCandidate('archive', `/${gameConfig.pathName}/archivio`)}
              >
                <span>Vai all'archivio</span>
                <FaIcon className="fas fa-calendar-alt"  />
              </Link>
            </div>
          </div>
        </section>

        {/* Game Catalog Section - showing other games */}
        <GameCatalogSection 
          excludeGameId={gameConfig.id}
          title="Altri Giochini"
          subtitle="Cerchi qualcos'altro? Potrebbe interessarti un altro dei nostri giochi."
        />
      </div>
    </PageShell>
  );
};

export default GameLandingPage;
